/**
 * Protobuf decoder for price stream messages.
 * Converts binary protobuf messages to the same JSON format expected by existing code.
 */

import { pricestream } from './pricestream.js';
import { storeOrderKey, verifyOrderMac } from './orderKeyManager';

const { PriceStreamMessage } = pricestream;

export interface DecodedMessage {
  type: 'orderUpdate' | 'gameUpdate' | 'matchedVolumeUpdate' | 'userOrderKeyUpdate';
  payload: unknown;
}

/**
 * Decodes a binary protobuf message and converts it to the JSON format
 * expected by the existing frontend code.
 */
export function decodeProtobufMessage(data: ArrayBuffer): DecodedMessage | null {
  try {
    const uint8Array = new Uint8Array(data);
    const message = PriceStreamMessage.decode(uint8Array);

    if (message.orderUpdate) {
      return {
        type: 'orderUpdate',
        payload: convertOrderUpdate(message.orderUpdate),
      };
    }

    if (message.gameUpdate) {
      return {
        type: 'gameUpdate',
        payload: convertGameUpdate(message.gameUpdate),
      };
    }

    if (message.matchedVolumeUpdate) {
      return {
        type: 'matchedVolumeUpdate',
        payload: convertMatchedVolumeUpdate(message.matchedVolumeUpdate),
      };
    }

    if (message.userOrderKeyUpdate) {
      handleUserOrderKeyUpdate(message.userOrderKeyUpdate);
      return {
        type: 'userOrderKeyUpdate',
        payload: null,
      };
    }

    console.warn('Unknown protobuf message type');
    return null;
  } catch (err) {
    console.error('Failed to decode protobuf message:', err);
    return null;
  }
}

/**
 * Handles UserOrderKeyUpdate messages by storing keys for MAC verification.
 */
function handleUserOrderKeyUpdate(update: pricestream.IUserOrderKeyUpdate): void {
  if (!update.keys) return;

  for (const key of update.keys) {
    if (key.epoch != null && key.key) {
      const keyBytes = key.key instanceof Uint8Array 
        ? key.key 
        : new Uint8Array(key.key);
      storeOrderKey(key.epoch, keyBytes);
    }
  }
}

/**
 * Converts protobuf OrderUpdate to the JSON format expected by processOrderUpdate
 */
function convertOrderUpdate(update: pricestream.IOrderUpdate): Record<string, unknown> {
  const epoch = update.epoch ?? 0;

  const result: Record<string, unknown> = {
    gameID: update.gameId,
    sport: update.sport,
    league: update.league,
    live: update.live,
    type: update.type,
    participantID: update.participantId,
    sideOrders: [] as unknown[],
  };

  // Optional fields
  if (update.market) result.market = update.market;
  if (update.side) result.side = update.side;
  if (update.ou) result.OU = update.ou;
  if (update.total != null) result.total = update.total;
  if (update.spread != null) result.spread = update.spread;
  if (update.mainHomeSpread != null) result.mainHomeSpread = update.mainHomeSpread;
  if (update.mainAwaySpread != null) result.mainAwaySpread = update.mainAwaySpread;
  if (update.mainTotal != null) result.mainTotal = update.mainTotal;
  // The session that triggered this update — used by placeTracker to
  // confirm full takes, where the order drops out of sideOrders.
  if (update.triggerOrderId) result.triggerOrderId = update.triggerOrderId;

  // Convert side orders with epoch for MAC verification
  if (update.sideOrders) {
    result.sideOrders = update.sideOrders.map(order => convertSideOrder(order, epoch));
  }

  return result;
}

/**
 * Converts protobuf SideOrder to the JSON format expected by the frontend.
 * Verifies MAC to determine if order belongs to current user.
 */
function convertSideOrder(order: pricestream.ISideOrder, epoch: number): Record<string, unknown> {
  const result: Record<string, unknown> = {
    id: order.id,
    type: order.type,
    sumUntaken: order.sumUntaken,
    odds: order.odds,
    bet: order.bet,
    gameID: order.gameId,
    takenRatio: order.takenRatio,
    participantID: order.participantId,
    gameStartExpiry: order.gameStartExpiry,
  };

  // Optional fields
  if (order.market) result.market = order.market;
  if (order.side) result.side = order.side;
  if (order.ou) result.OU = order.ou;
  if (order.total != null) result.total = order.total;
  if (order.spread != null) result.spread = order.spread;
  if (order.isPostArb) result.isPostArb = true;

  // MAC verification: set createdBy only if MAC matches current user
  if (order.id && order.mac) {
    const verifiedUserId = verifyOrderMac(epoch, order.id, order.mac);
    if (verifiedUserId) {
      result.createdBy = verifiedUserId;
    }
  }

  // Convert timestamps from unix milliseconds to ISO strings
  if (order.expiryUnixMs) {
    // Handle Long type from protobufjs
    const expiryMs = typeof order.expiryUnixMs === 'object' 
      ? (order.expiryUnixMs as { toNumber: () => number }).toNumber()
      : Number(order.expiryUnixMs);
    result.expiry = new Date(expiryMs).toISOString();
  }
  
  if (order.createdAtUnixMs) {
    const createdAtMs = typeof order.createdAtUnixMs === 'object'
      ? (order.createdAtUnixMs as { toNumber: () => number }).toNumber()
      : Number(order.createdAtUnixMs);
    result.createdAt = new Date(createdAtMs).toISOString();
  }

  return result;
}

/**
 * Converts protobuf GameUpdate to the JSON format expected by processGameUpdate
 */
function convertGameUpdate(update: pricestream.IGameUpdate): Record<string, unknown> {
  const result: Record<string, unknown> = {
    id: update.id,
    sport: update.sport,
    league: update.league,
    homeName: update.homeName,
    awayName: update.awayName,
    homeId: update.homeId,
    awayId: update.awayId,
    live: update.live,
    ended: update.ended,
    graded: update.graded,
    matchedVolume: update.matchedVolume,
  };

  // Convert start time
  if (update.startUnixMs) {
    const startMs = typeof update.startUnixMs === 'object'
      ? (update.startUnixMs as { toNumber: () => number }).toNumber()
      : Number(update.startUnixMs);
    result.start = new Date(startMs).toISOString();
  }

  // Optional scores
  if (update.homeScore != null) result.homeScore = update.homeScore;
  if (update.awayScore != null) result.awayScore = update.awayScore;

  return result;
}

/**
 * Converts protobuf MatchedVolumeUpdate to the JSON format expected by
 * processMatchedVolumeUpdate. The wire message is a complete, never-null
 * snapshot of one game's matched volume; matchedVolumeFactsFromUpdate reads
 * gameID, rootGameID, matchedVolume, and
 * rootGameMatchedVolumeIncludingChildren.
 */
function convertMatchedVolumeUpdate(update: pricestream.IMatchedVolumeUpdate): Record<string, unknown> {
  return {
    gameID: update.gameId,
    matchedVolume: update.matchedVolume,
    rootGameID: update.rootGameId,
    rootGameMatchedVolumeIncludingChildren:
      update.rootGameMatchedVolumeIncludingChildren,
  };
}
